import type { JSX } from 'react';
import type { CertificateVerification } from '../types/certificate.types';

export interface CertificateRefereeStatementProps {
  verification: CertificateVerification;
}

/**
 * A single referee's confirmation: who they are and what they stated.
 * Referee contact data is deliberately never rendered.
 */
export function CertificateRefereeStatement({
  verification,
}: CertificateRefereeStatementProps): JSX.Element {
  const { refereeName, refereeJobTitle, refereeComment, refereeAnswers } = verification;

  return (
    <li className="rounded-md border border-border/60 bg-background p-4">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-sm font-semibold text-foreground">{refereeName}</span>
        {refereeJobTitle && (
          <span data-testid="referee-job-title" className="text-xs text-muted-foreground">
            {refereeJobTitle}
          </span>
        )}
      </div>

      {refereeComment && (
        <blockquote
          data-testid="referee-comment"
          className="mt-2 border-l-2 border-primary/40 pl-3 text-sm italic text-muted-foreground"
        >
          {refereeComment}
        </blockquote>
      )}

      {refereeAnswers.length > 0 && (
        <dl data-testid="referee-answers" className="mt-3 space-y-2">
          {refereeAnswers.map((answer) => (
            <div key={answer.questionId}>
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {answer.questionText}
              </dt>
              <dd className="text-sm text-foreground">{answer.answerText}</dd>
            </div>
          ))}
        </dl>
      )}
    </li>
  );
}
